import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'

interface ToggleProps extends Omit<
  ComponentProps<'input'>,
  'type' | 'size' | 'checked' | 'onChange' | 'children'
> {
  checked: boolean
  onCheckedChange: (next: boolean) => void
  /** Required: a switch has no text of its own. */
  'aria-label': string
}

/**
 * The 44x28 switch (4-grid; was 26px).
 *
 * It IS an `<input type="checkbox" role="switch">`, not a styled div: space
 * toggles it, it submits with the form it sits in, and a screen reader
 * announces "switch, on" — three behaviours every hand-rolled toggle has to
 * reimplement and most get wrong. `appearance-none` strips the OS checkbox so
 * the input can be the track; the knob is a sibling rather than a `::before`
 * because pseudo-elements on a replaced element are not reliable across
 * engines, and `pointer-events-none` keeps it from stealing the click.
 *
 * The track is `muted` where the frames use the sunken grey: sunken has no
 * semantic alias, and in dark it is pure black — an off switch drawn in it
 * would vanish into the page. The knob is `popover`, the system's raised
 * surface, for the same reason.
 *
 * 28px is under the 44px touch minimum, so below `lg` the hit area is grown
 * VERTICALLY past the track by a transparent `::before`, as IconButton does.
 * Vertically only: the track is already 44px wide, and switches sit at the end
 * of settings rows where a wider target would reach into the row above.
 *
 * The 28px track and the unchanged 20px knob leave a clean 4px inset on every
 * side — `top-1`/`left-1`, no arbitrary value needed — and a checked travel
 * of exactly 16px (`translate-x-4`): 44 − 4 − 20 − 4.
 */
export default function Toggle({
  checked,
  onCheckedChange,
  disabled,
  className,
  ...props
}: ToggleProps) {
  return (
    <label
      className={cn(
        'group relative inline-flex h-7 w-11 shrink-0 cursor-pointer touch-manipulation',
        "before:absolute before:-inset-y-2 before:left-0 before:w-full before:content-[''] lg:before:hidden",
        'has-[:disabled]:pointer-events-none has-[:disabled]:opacity-50',
        className,
      )}
    >
      <input
        {...props}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(event) => {
          onCheckedChange(event.target.checked)
        }}
        className="peer size-full cursor-pointer appearance-none rounded-full border border-border bg-muted transition-colors checked:border-transparent checked:bg-tone-accent-soft not-checked:group-hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute top-1 left-1 size-5 rounded-full border border-border bg-popover transition-transform peer-checked:translate-x-4 peer-checked:border-transparent peer-checked:bg-accent peer-checked:group-hover:bg-accent/90"
      />
    </label>
  )
}
